import React, { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, AlertCircle } from "lucide-react";
import { useGetContributorDashboard } from "../lib/api/contributorDashboard";
import { useAuth } from "../context/AuthContext";
import WelcomeCard from "../components/dashboard/WelcomeCard";
import StatsGrid from "../components/dashboard/StatsGrid";
import ActiveProjectsList from "../components/dashboard/ActiveProjectsList";
import RecentApplications from "../components/dashboard/RecentApplications";
import ProfileCard from "../components/dashboard/ProfileCard";
import EarningsCard from "../components/dashboard/EarningsCard";
import QuickActions from "../components/dashboard/QuickActions";
import PendingTasksCard from "../components/dashboard/PendingTasksCard";
import AvailableProjectsCard from "../components/dashboard/AvailableProjectsCard";

const ContributorDashboard: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?._id || user?.id || "";

  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
    isRefetching,
  } = useGetContributorDashboard(userId, { period: "month" });

  // const markAsReadMutation = useMarkNotificationAsRead();
  // const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: ["contributor-dashboard", userId],
    });
  };

  // const handleMarkNotificationAsRead = (notificationId: string) => {
  //   markAsReadMutation.mutate({ userId, notificationId });
  // };

  // const handleMarkAllAsRead = () => {
  //   markAllAsReadMutation.mutate({ userId });
  // };

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="">
          <div className="bg-white rounded-xl p-8 text-center shadow-sm">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Failed to Load Dashboard
            </h2>
            <p className="text-gray-500 mb-4">
              {(error as any)?.message ||
                "Something went wrong. Please try again."}
            </p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="space-y-6">
        {/* Header with Refresh */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <button
            onClick={handleRefresh}
            disabled={isRefetching}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefetching ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        {/* Welcome Card */}
        <WelcomeCard
          firstName={dashboardData?.user?.firstName || ""}
          lastName={dashboardData?.user?.lastName || ""}
          memberSince={
            dashboardData?.user?.memberSince || new Date().toISOString()
          }
          level={dashboardData?.levelInfo?.currentLevel || 1}
          xp={dashboardData?.levelInfo?.currentXP || 0}
          xpToNextLevel={dashboardData?.levelInfo?.xpToNextLevel || 100}
          progressPercentage={dashboardData?.levelInfo?.progressPercentage || 0}
          loading={isLoading}
        />

        {/* Stats Grid */}
        <StatsGrid
          stats={{
            activeProjects: dashboardData?.stats?.activeProjects || 0,
            completedProjects: dashboardData?.stats?.completedProjects || 0,
            pendingApplications: dashboardData?.stats?.pendingApplications || 0,
            totalEarnings: dashboardData?.stats?.totalEarnings || 0,
            successRate: dashboardData?.stats?.successRate || 0,
            averageRating: dashboardData?.stats?.averageRating || 0,
          }}
          loading={isLoading}
        />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            {/* Active Projects */}
            <ActiveProjectsList
              projects={dashboardData?.activeProjects || []}
              loading={isLoading}
            />

            {/* Recent Applications */}
            <RecentApplications
              applications={dashboardData?.recentApplications || []}
              loading={isLoading}
            />
          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-6">
            {/* Profile Verification */}
            <ProfileCard
              profile={
                dashboardData?.profile || {
                  completionPercentage: 0,
                  isFullyVerified: false,
                  verificationSteps: [],
                  pendingSteps: [],
                }
              }
              loading={isLoading}
            />

            {/* Earnings */}
            <EarningsCard
              earnings={
                dashboardData?.earnings || {
                  totalEstimated: 0,
                  fromCompleted: 0,
                  fromActive: 0,
                  availableBalance: 0,
                  avgHourlyRate: 0,
                  totalHoursWorked: 0,
                }
              }
              loading={isLoading}
            />
          </div>
        </div>

        {/* Secondary Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Pending Tasks */}
          <PendingTasksCard
            tasks={dashboardData?.pendingTasks || []}
            loading={isLoading}
          />

          {/* Available Projects */}
          <AvailableProjectsCard
            data={
              dashboardData?.availableProjects || {
                total: 0,
                newThisWeek: 0,
                topCategories: [],
              }
            }
            loading={isLoading}
          />

          {/* Quick Actions */}
          <QuickActions />
        </div>
      </div>
    </div>
  );
};

export default ContributorDashboard;
